/**
 * Hint / teacher layer.
 *
 * Wraps the technique engine to deliver Good Sudoku's signature feature: the
 * next *logical* move plus a short lesson on the technique behind it. Returns
 * the cells/units to highlight so the UI can draw the player's attention.
 */

import { Grid, UNITS, colOf, rowOf } from "./board.js";
import { DifficultyId, tierToDifficulty } from "./difficulty.js";
import {
  Step,
  TECHNIQUE_NAME,
  TECHNIQUE_TIER,
  TechniqueId,
  createSolverState,
  nextStep,
} from "./techniques.js";

export interface Hint {
  step: Step;
  technique: TechniqueId;
  title: string;
  tier: number;
  difficulty: DifficultyId;
  /** One-line "what to do". */
  summary: string;
  /** Teaching paragraph: the "why". */
  explanation: string;
  highlightCells: number[];
  highlightUnits: number[];
  digits: number[];
}

const cellName = (i: number): string => `R${rowOf(i) + 1}C${colOf(i) + 1}`;

function unitName(u: number): string {
  if (u < 9) return `row ${u + 1}`;
  if (u < 18) return `column ${u - 9 + 1}`;
  return `box ${u - 18 + 1}`;
}

const listCells = (cells: number[]): string => cells.map(cellName).join(", ");

/** Human-readable "what to do" + "why" for a single technique step. */
export function explainStep(step: Step): { summary: string; explanation: string } {
  const ds = step.digits.join(", ");
  switch (step.technique) {
    case "nakedSingle": {
      const p = step.placements[0];
      return {
        summary: `Place ${p.digit} in ${cellName(p.cell)}.`,
        explanation: `${cellName(p.cell)} has only one candidate left, every other digit is already used by a peer in its row, column or box, so it must be ${p.digit}. This is a Naked Single, the most basic deduction in Sudoku.`,
      };
    }
    case "hiddenSingle": {
      const p = step.placements[0];
      const u = step.units[0];
      return {
        summary: `Place ${p.digit} in ${cellName(p.cell)}.`,
        explanation: `Within ${unitName(u)}, the digit ${p.digit} can legally go in only one cell, ${cellName(p.cell)}. Even though that cell has other candidates, ${p.digit} has nowhere else to live in this house, so it's a Hidden Single.`,
      };
    }
    case "pointing": {
      return {
        summary: `Remove ${ds} from the highlighted line.`,
        explanation: `In ${unitName(step.units[0])}, candidate ${ds} only appears along one line (${unitName(step.units[1])}). Whichever cell takes ${ds} in the box, it must sit on that line, so ${ds} can be eliminated from the rest of the line outside the box. This is a Pointing Pair/Triple (locked candidates).`,
      };
    }
    case "claiming": {
      return {
        summary: `Remove ${ds} from the rest of the box.`,
        explanation: `In ${unitName(step.units[0])}, candidate ${ds} is confined to a single box (${unitName(step.units[1])}). So ${ds} must be placed within that overlap, and can be removed from the box's other cells. This is Box/Line Reduction.`,
      };
    }
    case "nakedPair":
    case "nakedTriple":
    case "nakedQuad": {
      return {
        summary: `Remove ${ds} from elsewhere in the unit.`,
        explanation: `Cells ${listCells(step.focusCells)} together hold only the candidates ${ds}. Those ${step.focusCells.length} cells must use up exactly those ${step.focusCells.length} digits between them, so ${ds} can't appear anywhere else in ${unitName(step.units[0])}. This is a ${TECHNIQUE_NAME[step.technique]}.`,
      };
    }
    case "hiddenPair":
    case "hiddenTriple":
    case "hiddenQuad": {
      return {
        summary: `Lock ${ds} into the highlighted cells.`,
        explanation: `In ${unitName(step.units[0])}, the digits ${ds} can only appear in cells ${listCells(step.focusCells)}. Those cells are therefore reserved for ${ds}, so every other candidate can be stripped out of them. This is a ${TECHNIQUE_NAME[step.technique]}.`,
      };
    }
    case "xWing":
    case "swordfish":
    case "jellyfish": {
      const size = step.technique === "xWing" ? 2 : step.technique === "swordfish" ? 3 : 4;
      return {
        summary: `Remove ${ds} from the covered lines.`,
        explanation: `Digit ${ds} forms a ${TECHNIQUE_NAME[step.technique]}: across ${size} base lines it appears only within ${size} crossing lines, forming a locked rectangle/grid. Because those ${size} lines must each place ${ds} inside the pattern, ${ds} can be eliminated from the crossing lines everywhere else. This is a classic fish pattern.`,
      };
    }
    case "xyWing": {
      const [x, y, z] = step.digits;
      const [pivot, a, b] = step.focusCells;
      return {
        summary: `Remove ${z} from cells seeing both wings.`,
        explanation: `${cellName(pivot)} is a pivot holding {${x},${y}}. Its wings ${cellName(a)} and ${cellName(b)} hold {${x},${z}} and {${y},${z}}. Whatever the pivot becomes, one wing is forced to ${z}, so any cell that sees BOTH wings cannot be ${z}. This is an XY-Wing.`,
      };
    }
    case "xyzWing": {
      const z = step.eliminations[0]?.digit;
      const [pivot, a, b] = step.focusCells;
      return {
        summary: `Remove ${z} from cells seeing the pivot and both wings.`,
        explanation: `${cellName(pivot)} holds three candidates, with wings ${cellName(a)} and ${cellName(b)} sharing ${z}. One of these three cells must be ${z}, so any cell seeing all three loses ${z}. This is an XYZ-Wing.`,
      };
    }
    case "skyscraper": {
      const d = step.digits[0];
      return {
        summary: `Remove ${d} from cells seeing both roof ends.`,
        explanation: `Digit ${d} appears exactly twice in two lines that share one "base" column/row. The two far ends (the roof) mean ${d} must land on one of them, so any cell that sees BOTH roof ends can't be ${d}. This is a Skyscraper.`,
      };
    }
    case "twoStringKite": {
      const d = step.digits[0];
      const t = step.eliminations[0]?.cell;
      return {
        summary: `Remove ${d} from ${t !== undefined ? cellName(t) : "the target cell"}.`,
        explanation: `A row and a column each contain ${d} exactly twice, and one end of each shares a box (the link). Whichever way it resolves, ${d} ends up at one of the two far ends, so the cell seeing both far ends can't be ${d}. This is a Two-String Kite.`,
      };
    }
    case "wWing": {
      const [x, y] = step.digits;
      const w = step.eliminations[0]?.digit;
      const [a, b] = step.focusCells;
      return {
        summary: `Remove ${w} from cells seeing both ${cellName(a)} and ${cellName(b)}.`,
        explanation: `${cellName(a)} and ${cellName(b)} both hold {${x},${y}} and are joined by a strong link on ${w === x ? y : x}. Together they guarantee one of them is ${w}, so any cell seeing both loses ${w}. This is a W-Wing.`,
      };
    }
    case "uniqueRectangle": {
      const [x, y] = step.digits;
      return {
        summary: `Remove ${x} and ${y} from the fourth corner.`,
        explanation: `Three corners of a rectangle spanning two boxes hold only {${x},${y}}. If the fourth corner were also just {${x},${y}}, the puzzle would have two solutions, which a proper puzzle can't. So the fourth corner must take one of its extra candidates, and ${x}/${y} can be removed from it. This is a Unique Rectangle.`,
      };
    }
    case "fullHouse": {
      const p = step.placements[0];
      return {
        summary: `Place ${p.digit} in ${cellName(p.cell)}.`,
        explanation: `${unitName(step.units[0])} has just one empty cell left, ${cellName(p.cell)}, so the one missing digit, ${p.digit}, must go there. This is a Full House.`,
      };
    }
    case "uniqueRectangleType2": {
      const z = step.eliminations[0]?.digit;
      const [x, y] = step.digits;
      return {
        summary: `Remove ${z} from cells seeing both roof cells.`,
        explanation: `Four cells form a rectangle in two boxes; two hold {${x},${y}} and the other two hold {${x},${y},${z}}. To avoid two solutions, one of those two must be ${z}, so any cell seeing both loses ${z}. This is a Unique Rectangle (Type 2).`,
      };
    }
    case "uniqueRectangleType4": {
      const locked = step.digits[0];
      const other = step.eliminations[0]?.digit;
      return {
        summary: `Remove ${other} from the two roof cells.`,
        explanation: `In a Unique Rectangle, digit ${locked} can only go in the two roof cells within ${unitName(step.units[0])}. If ${other} also went in one of them the puzzle would have two solutions, so ${other} can be removed from both. This is a Unique Rectangle (Type 4).`,
      };
    }
    case "bug": {
      const p = step.placements[0];
      return {
        summary: `Place ${p.digit} in ${cellName(p.cell)}.`,
        explanation: `Every unsolved cell has exactly two candidates except ${cellName(p.cell)}, which has three. A puzzle with one solution can't be an all-bivalue "grave", so the odd digit out, ${p.digit}, the one appearing three times in ${unitName(step.units[0])}, must be placed. This is BUG + 1.`,
      };
    }
    case "simpleColoring": {
      const d = step.digits[0];
      return {
        summary: `Remove ${d} from cells that see both colors.`,
        explanation: `Following ${d} through its conjugate pairs (units where it fits in only two cells) and coloring them alternately, one color must be true. Any cell that can see both colors therefore can't be ${d}. This is Simple coloring.`,
      };
    }
    case "remotePairs": {
      const [x, y] = step.digits;
      return {
        summary: `Remove ${x} and ${y} from cells seeing both ends.`,
        explanation: `A chain of cells all holding {${x},${y}}, each seeing the next, must alternate between the two digits. Any cell that sees an "${x}-end" and a "${y}-end" of the chain can be neither, so both are removed. This is Remote Pairs.`,
      };
    }
    case "emptyRectangle": {
      const d = step.digits[0];
      const t = step.eliminations[0]?.cell;
      return {
        summary: `Remove ${d} from ${t !== undefined ? cellName(t) : "the target cell"}.`,
        explanation: `In ${unitName(step.units[0])}, every candidate ${d} lies on one row and one column, leaving an "empty rectangle". Combined with a conjugate pair on ${d} elsewhere, whichever way it resolves, ${d} is forced into that row or column, so the cell where the free end of the pair crosses the rectangle's line can't be ${d}. This is an Empty Rectangle.`,
      };
    }
    case "finnedXWing":
    case "finnedSwordfish": {
      const d = step.digits[0];
      return {
        summary: `Remove ${d} from cells sharing the fin's box.`,
        explanation: `This is an almost-${step.technique === "finnedXWing" ? "X-Wing" : "Swordfish"}: a clean fish on ${d} spoiled by one extra candidate, the "fin", tucked in a single box. The normal fish eliminations only survive where they ALSO see the fin (i.e. share its box), but that's still enough to remove ${d} there. This is a ${TECHNIQUE_NAME[step.technique]}.`,
      };
    }
    case "hiddenRectangle": {
      const elim = step.eliminations[0];
      return {
        summary: `Remove ${elim?.digit} from ${elim ? cellName(elim.cell) : "the corner"}.`,
        explanation: `Four cells form a rectangle in two boxes; one corner is the bivalue pair {${step.digits.join(", ")}}. In the opposite corner's row and column, one of those digits can land only at the two rectangle corners (a strong link each way). If the other digit went in that corner, the puzzle would have two solutions, so it can be removed. This is a Hidden Rectangle.`,
      };
    }
    case "xyChain": {
      const z = step.digits[0];
      return {
        summary: `Remove ${z} from cells seeing both ends of the chain.`,
        explanation: `A chain of bivalue cells, each sharing one digit with the next, runs from one ${z} to another. Following it, if the first end isn't ${z} the last end must be, so one end is always ${z}. Any cell seeing both ends therefore can't be ${z}. This is an XY-Chain.`,
      };
    }
    case "xChain": {
      const d = step.digits[0];
      return {
        summary: `Remove ${d} from cells seeing both ends of the chain.`,
        explanation: `Following ${d} along alternating strong links (a house where it fits in just two cells) and weak links (a shared house), the chain's two ends behave like a single strong link: at least one of them must be ${d}. So any cell that sees both ends can't be ${d}. This is an X-Chain.`,
      };
    }
    case "wxyzWing": {
      const z = step.eliminations[0]?.digit;
      return {
        summary: `Remove ${z} from cells seeing every ${z} in the wing.`,
        explanation: `Four cells holding only four digits form a WXYZ-Wing: a three-cell almost-locked set linked to a bivalue cell by a restricted digit. Because that link can be true in only one place, one of the four cells is forced to be ${z}, so any cell seeing all of the wing's ${z} candidates can't be ${z}. This is a WXYZ-Wing.`,
      };
    }
    case "alsXZ": {
      const [x, z] = step.digits;
      return {
        summary: `Remove ${z} from cells seeing every ${z} in both sets.`,
        explanation: `Two Almost Locked Sets share a restricted common digit ${x} (it can land in only one of the sets). Whichever set misses out then becomes fully locked and must place ${z}, so ${z} is guaranteed in one set or the other, and any cell seeing every ${z} in both sets can't be ${z}. This is an ALS-XZ.`,
      };
    }
    default: {
      return { summary: "Apply the highlighted technique.", explanation: "" };
    }
  }
}

/**
 * Compute the next logical hint for the current board position.
 * `maxTier` can cap the difficulty of techniques offered.
 */
export function getHint(grid: Grid, maxTier = 5): Hint | null {
  const state = createSolverState(grid);
  const step = nextStep(state, maxTier);
  if (!step) return null;
  const { summary, explanation } = explainStep(step);
  const tier = TECHNIQUE_TIER[step.technique];

  // Highlight the pattern cells, eliminations and any units involved.
  const highlightCells = new Set<number>(step.focusCells);
  for (const e of step.eliminations) highlightCells.add(e.cell);
  for (const p of step.placements) highlightCells.add(p.cell);
  const highlightUnits = [...new Set(step.units)];

  return {
    step,
    technique: step.technique,
    title: TECHNIQUE_NAME[step.technique],
    tier,
    difficulty: tierToDifficulty(tier),
    summary,
    explanation,
    highlightCells: [...highlightCells],
    highlightUnits,
    digits: step.digits,
  };
}

/** Convenience: the unit's member cells (for highlighting whole houses). */
export function unitCells(u: number): readonly number[] {
  return UNITS[u];
}
